import { useMemo } from "react";

import { getIsInSupportedLocation } from "~/v2/utils/cryptoLocation";
import type { Owner } from "../rest/account";
import { useGetCurrentAccountIfLiveElseTradingAccount } from "./useGetAccount";
import { useGetOwnerDetails } from "./useGetOwnerDetails";
import { useGetOwners } from "./useGetOwners";

/**
 * Determines whether crypto transfers are supported for the current
 * trading account.
 *
 * For legal entity accounts the entity owner's location is used
 * rather than the account opener's (which is what /accounts/:id/details
 * returns).
 *
 * If the crypto agreement is signed location check is not needed
 */
export const useIsCryptoSupported = () => {
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();
  const accountId = account?.id;

  const { details, isLoading: isDetailsLoading } = useGetOwnerDetails();

  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: accountId ?? "",
    options: { enabled: !!accountId },
  });

  const isCryptoSupported = useMemo(() => {
    if (!account || !owners || !details) {
      return false;
    }

    if (account.account_type === "ira") {
      return false;
    }

    const entityOwner = owners.find(
      (owner: Owner) => owner.is_legal_entity && owner.primary,
    );

    // if crypto agreement is signed location check is not needed
    if (details.crypto_agreement_signed_at) {
      return true;
    }

    if (entityOwner) {
      return getIsInSupportedLocation(
        entityOwner.country_of_tax_residence ?? "",
        entityOwner.state ?? "",
      );
    }

    return getIsInSupportedLocation(
      details.country_of_tax_residence ?? "",
      details.state ?? "",
    );
  }, [account, owners, details]);

  const isLoading = isDetailsLoading || isOwnersLoading;

  return { isCryptoSupported, isLoading };
};
